import { z } from 'zod';
import { DateOnlySchema, IdSchema, ListQuerySchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
export const PropertyOccupancySchema = z.enum([
    'residential',
    'commercial',
    'multi_family',
    'industrial',
    'public_facility',
    'other',
]);
export const PropertyPartyRoleSchema = z.enum([
    'owner',
    'occupant',
    'property_manager',
    'billing',
    'referral_source',
]);
/** A party is exactly one canonical company or contact. */
export const PartyReferenceSchema = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('company'), companyId: IdSchema }),
    z.strictObject({ type: z.literal('contact'), contactId: IdSchema }),
]);
export const PropertyPartySchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    propertyId: IdSchema,
    role: PropertyPartyRoleSchema,
    party: PartyReferenceSchema,
    effectiveFrom: DateOnlySchema.nullable(),
    effectiveTo: DateOnlySchema.nullable(),
    notes: z.string().nullable(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const PropertySchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    label: z.string().nullable(),
    addressLine1: z.string(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    postalCode: z.string().nullable(),
    countryCode: z.string().length(2).nullable(),
    occupancy: PropertyOccupancySchema,
    accessInstructions: z.string().nullable(),
    notes: z.string().nullable(),
    archivedAt: TimestampSchema.nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const CreatePropertyRequestSchema = z.strictObject({
    label: z.string().trim().max(200).nullable().default(null),
    addressLine1: z.string().trim().min(1).max(200),
    addressLine2: z.string().trim().max(200).nullable().default(null),
    city: z.string().trim().max(100).nullable().default(null),
    region: z.string().trim().max(100).nullable().default(null),
    postalCode: z.string().trim().max(30).nullable().default(null),
    countryCode: z.string().trim().length(2).toUpperCase().nullable().default(null),
    occupancy: PropertyOccupancySchema.default('residential'),
    accessInstructions: z.string().trim().max(2000).nullable().default(null),
    notes: z.string().trim().max(10_000).nullable().default(null),
});
export const UpdatePropertyRequestSchema = CreatePropertyRequestSchema.partial().extend({
    version: VersionSchema,
});
export const PropertyListQuerySchema = ListQuerySchema.extend({
    includeArchived: z.stringbool().default(false),
});
export const PropertyListSchema = z.strictObject({
    items: z.array(PropertySchema),
    page: PageInfoSchema,
});
export const CreatePropertyPartyRequestSchema = z
    .strictObject({
    role: PropertyPartyRoleSchema,
    party: PartyReferenceSchema,
    effectiveFrom: DateOnlySchema.nullable().default(null),
    effectiveTo: DateOnlySchema.nullable().default(null),
    notes: z.string().trim().max(1000).nullable().default(null),
})
    .refine((value) => !value.effectiveFrom || !value.effectiveTo || value.effectiveFrom <= value.effectiveTo, { message: 'effectiveTo must not be before effectiveFrom', path: ['effectiveTo'] });
export const PropertyPartyListSchema = z.strictObject({
    items: z.array(PropertyPartySchema),
    page: PageInfoSchema,
});
